import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { VSwitch } from '@client/components/_shared/forms/view-form/VSwitch';
import { VTextInput } from '@client/components/_shared/forms/view-form/VTextInput';
import { DndContainer } from '@client/components/_ui/data-display/dnd/DndContainer';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { FieldConfigurationList } from '@client/components/field-configurations/FieldConfigurationList';
import { createFormStore } from '@client/stores/FormStore';
import { trpc } from '@client/trpc/client';
import { PRODUCT_TYPE_TEMPLATE_LABELS, type ProductTypeTemplate } from '@shared/constants';
import type { ProductTypeDef } from '@shared/constants/business/product-type';
import { isTruthy, uuid } from '@shared/utils';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Collapse, Divider, Dropdown, Skeleton, Tag } from 'antd';
import { ChevronDown, ChevronRight, FileText, LayoutTemplate, Plus } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/edit/$productTypeId/data-fields',
)({
    staticData: {
        presentation: 'overlay',
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ProductTypeDataEdit />
        </PageController>
    );
}

const productTypeDataFormStore = createFormStore<ProductTypeDef>();

const TEMPLATE_OPTIONS: {
    id: ProductTypeTemplate;
    label: string;
    description: string;
}[] = [
    {
        id: 'DRIVER_CONTACT',
        label: 'Driver contact',
        description: 'Collect driver information',
    },
];

function ProductTypeDataEdit() {
    const { productTypeId } = Route.useParams();
    const { data, isPending, error, refetch } = trpc.productType.getProductType.useQuery({ id: productTypeId });
    const { mutateAsync: updateProductType } = trpc.productType.updateProductType.useMutation();
    const { mutate: deleteProductTypeCard } = trpc.productType.deleteProductTypeCard.useMutation();

    const [defaultActiveKey, setDefaultActiveKey] = useState<string | undefined>(undefined);

    const { formData, save } = productTypeDataFormStore.useInit({
        entityLabel: 'Product type data',
        entity: 'productType',
        data: data?.definition || undefined,
        isLoading: isPending,

        async onReload() {
            await refetch();
        },

        async onSave(currentFormData, changedValues) {
            try {
                message.loading('Saving...');

                const cardIndex = Array.isArray(changedValues.cards) ? NaN : +Object.keys(changedValues.cards || {})[0];

                if (typeof cardIndex === 'number' && !Number.isNaN(cardIndex)) {
                    await updateProductType({
                        id: productTypeId,
                        set: {
                            definition: {
                                ...currentFormData,
                                cards:
                                    currentFormData.cards?.map((card, i) =>
                                        i === cardIndex
                                            ? {
                                                  ...card,
                                                  ...changedValues.cards?.[cardIndex],
                                              }
                                            : card,
                                    ) || [],
                            },
                        },
                    });

                    message.success('Saved.');

                    return { success: true };
                }

                await updateProductType({
                    id: productTypeId,
                    set: {
                        definition: {
                            ...currentFormData,
                            ...changedValues,
                        },
                    },
                });

                message.success('Saved.');

                return { success: true };
            } catch (e) {
                return { success: false, error: e };
            }
        },
        async onError(error) {
            errorMessage.show(error);
        },
    });

    if (!formData || data?.id !== productTypeId) {
        return (
            <TRPCErrorBoundary error={error}>
                <Skeleton active />
            </TRPCErrorBoundary>
        );
    }

    return (
        <TRPCErrorBoundary error={error}>
            <Outlet />

            <div className="grid grid-cols-4 gap-4">
                <VTextInput
                    label="Tab label"
                    field="claimTabLabel"
                    formStore={productTypeDataFormStore}
                    rules={[{ required: true }]}
                />
            </div>

            <Divider titlePlacement="left" className="my-4 text-sm text-subtle-foreground">
                Cards ({formData?.cards?.length || 0})
            </Divider>

            <DndContainer
                ids={formData?.cards?.map((item) => item.id) || []}
                onDragEnd={async (keys) => {
                    await save({
                        ...formData,
                        cards: keys.map((key) => formData.cards?.find((item) => item.id === key)).filter(isTruthy),
                    });
                }}
                renderItem={(props) => {
                    const i = props.index;
                    const item = formData?.cards?.[i];

                    if (!item) {
                        return null;
                    }

                    return (
                        <Collapse
                            key={item.id}
                            size="small"
                            defaultActiveKey={defaultActiveKey}
                            classNames={{
                                root: 'transition-shadow hover:shadow-sm mb-4',
                                header: 'items-center',
                                icon: 'mr-2',
                                body: 'py-4 px-6',
                            }}
                            expandIcon={({ isActive }) =>
                                isActive ? (
                                    <ChevronDown className="size-4 text-subtle-foreground" />
                                ) : (
                                    <ChevronRight className="size-4 text-subtle-foreground" />
                                )
                            }
                            items={[
                                {
                                    key: item.id,
                                    label: (
                                        <div className="flex items-center gap-2">
                                            {<props.DragButton />}
                                            <Tag color={item.type === 'TEMPLATE' ? 'blue' : 'green'} variant="outlined">
                                                {item.type === 'TEMPLATE'
                                                    ? `Template: ${PRODUCT_TYPE_TEMPLATE_LABELS[item.templateId]}`
                                                    : 'Custom form'}
                                            </Tag>

                                            {item.title && <span className="text-subtle-foreground">{item.title}</span>}
                                        </div>
                                    ),
                                    extra: (
                                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                            <Button.Delete
                                                onConfirm={async (onSuccess) =>
                                                    deleteProductTypeCard(
                                                        {
                                                            productTypeId,
                                                            cardId: item.id,
                                                        },
                                                        {
                                                            onSuccess() {
                                                                void refetch();
                                                                onSuccess();
                                                            },
                                                            onError(error) {
                                                                errorMessage.show(error);
                                                            },
                                                        },
                                                    )
                                                }
                                            />
                                        </div>
                                    ),
                                    children: (
                                        <>
                                            <div className="grid grid-cols-3 gap-4">
                                                <VTextInput
                                                    label="Title"
                                                    field={`cards.${i}.title`}
                                                    formStore={productTypeDataFormStore}
                                                    rules={[{ required: true }]}
                                                />
                                                <VTextInput
                                                    label="Subtitle"
                                                    field={`cards.${i}.subTitle`}
                                                    formStore={productTypeDataFormStore}
                                                />
                                                <VSwitch
                                                    label="Allow skipping"
                                                    field={`cards.${i}.allowSkipping`}
                                                    formStore={productTypeDataFormStore}
                                                />
                                            </div>
                                            {item.type === 'FORM' && (
                                                <>
                                                    <FieldConfigurationList
                                                        entity="PRODUCT_TYPE"
                                                        entityId={productTypeId}
                                                        section={item.id}
                                                        showEntityConfiguration
                                                        showIsRequired
                                                    />
                                                </>
                                            )}
                                        </>
                                    ),
                                },
                            ]}
                        />
                    );
                }}
            />

            <Dropdown
                placement="top"
                trigger={['click']}
                menu={{
                    items: [
                        {
                            key: 'custom',
                            label: (
                                <div className="flex items-center gap-3 py-1">
                                    <div className="flex size-8 items-center justify-center rounded-lg bg-green-100">
                                        <FileText className="size-4 text-green-600" />
                                    </div>
                                    <div>
                                        <div className="font-medium">Custom form</div>
                                        <div className="text-subtle-foreground text-xs">
                                            Create a card with custom fields
                                        </div>
                                    </div>
                                </div>
                            ),
                            async onClick() {
                                const id = uuid();
                                setDefaultActiveKey(id);

                                await save({
                                    ...formData,
                                    cards: [
                                        ...(formData.cards || []),
                                        {
                                            id,
                                            type: 'FORM',
                                            title: '',
                                        },
                                    ],
                                });
                            },
                        },
                        { type: 'divider' },
                        {
                            key: 'template',
                            label: (
                                <span className="font-medium text-subtle-foreground text-xs uppercase">Templates</span>
                            ),
                            disabled: true,
                        },
                        ...TEMPLATE_OPTIONS.map((item) => ({
                            key: item.id,
                            label: (
                                <div className="flex items-center gap-3 py-1">
                                    <div className="flex size-8 items-center justify-center rounded-lg bg-blue-100">
                                        <LayoutTemplate className="size-4 text-blue-600" />
                                    </div>
                                    <div>
                                        <div className="font-medium">{item.label}</div>
                                        <div className="text-subtle-foreground text-xs">{item.description}</div>
                                    </div>
                                </div>
                            ),
                            async onClick() {
                                const id = uuid();
                                setDefaultActiveKey(id);

                                await save({
                                    ...formData,
                                    cards: [
                                        ...(formData.cards || []),
                                        {
                                            id,
                                            type: 'TEMPLATE',
                                            templateId: item.id,
                                            title: item.label,
                                        },
                                    ],
                                });
                            },
                        })),
                    ],
                }}
            >
                <Button type="dashed" size="large" icon={<Plus />} className="w-full">
                    Add card
                </Button>
            </Dropdown>
        </TRPCErrorBoundary>
    );
}
